import {
	type PartCodingAttachmentOutcome,
	type PartCodingMatchCandidate,
	type PartCodingMatchResponse,
	type PartCodingMatchRow
} from '../../models/part-coding';
import {
	type PartCodingCandidate,
	type PartCodingRow,
	type PartCodingStatus
} from '../../models/part-coding.model';

// Customer specific searches that parent's basket. Generic and Strict do not.
// History still uses the parent id on either scope.
export function scopeParentCompanyId(
	parentId: string | null,
	scope: CodingScope
): string | null {
	if (scope !== 'customer' || !parentId) return null;
	return parentId;
}

export const EXACT_DATA_CONVERTER_REASON = 'exact-data-converter';
export const FUZZY_DATA_CONVERTER_REASON = 'fuzzy-data-converter';
export const OUTSIDE_MARKET_BASKET_REASON = 'outside-market-basket';
export const NO_EXACT_HISTORY_REASON = 'no-exact-history';

export const HISTORICAL_MATCH_SOURCE = 'Historical Match';
export const MARKET_BASKET_SOURCE = 'Market Basket';
export const ITEM_MASTER_SOURCE = 'Item master';

// Token-sort history uses a tighter Auto line than the catalog's 75.
export const HISTORY_FUZZY_AUTO = 95;

export type CodingScope = 'customer' | 'generic' | 'strict';

export const CODING_SCOPES: readonly { id: CodingScope; label: string }[] = [
	{ id: 'customer', label: 'Customer specific' },
	{ id: 'generic', label: 'Generic' },
	{ id: 'strict', label: 'Strict — No Substitutes' }
];

export function statusForMatch(
	row: PartCodingMatchRow,
	lowConfidenceThreshold: number
): PartCodingStatus {
	if (!row.matchFound) {
		return row.reasonCode === NO_EXACT_HISTORY_REASON ? 'Review' : 'RFQ';
	}
	if (row.quantityAvailable === 0) return 'Out of Stock';
	if (row.reasonCode === FUZZY_DATA_CONVERTER_REASON)
		return (row.confidence ?? 0) >= HISTORY_FUZZY_AUTO ? 'Auto' : 'Review';
	if (row.reasonCode === OUTSIDE_MARKET_BASKET_REASON) return 'Review';
	if ((row.confidence ?? 0) < lowConfidenceThreshold) return 'Review';
	return 'Auto';
}

export function rowsFromMatch(
	response: PartCodingMatchResponse
): PartCodingRow[] {
	const marketBasket = response.sourceList === 'marketbasket';

	return response.rows.map((row) => {
		const matched = row.matchFound;
		const exactHistoryMiss = row.reasonCode === NO_EXACT_HISTORY_REASON;
		const historyHit =
			row.reasonCode === EXACT_DATA_CONVERTER_REASON ||
			row.reasonCode === FUZZY_DATA_CONVERTER_REASON;
		const outsideBasket = row.reasonCode === OUTSIDE_MARKET_BASKET_REASON;
		let marketBasketFlag: boolean | null = null;
		if (historyHit || outsideBasket) marketBasketFlag = false;
		else if (matched) marketBasketFlag = marketBasket;
		return {
			id: String(row.lineIndex),
			mark: Number(row.markNumber ?? row.lineIndex + 1),
			quantity: row.quantity,
			customerDescription: row.customerDescription,
			status: statusForMatch(row, response.lowConfidenceThreshold),
			partCode: matched ? (row.partCode ?? '') : '',
			rkDescription: matched ? (row.rkDescription ?? '') : '',
			inventory: row.quantityAvailable,
			isMarketBasket: marketBasketFlag,
			source: exactHistoryMiss
				? ''
				: lineSource(historyHit, outsideBasket, marketBasket),
			confidence: row.confidence ?? 0,
			reason: row.reason ?? '',
			candidateFamily: '',
			candidates: (row.candidates ?? []).map(toCandidate),
			codingLineId: row.codingLineId ?? null,
			repCheck: 'pending'
		};
	});
}

export function lineSource(
	historyHit: boolean,
	outsideBasket: boolean,
	marketBasket: boolean
): string {
	if (historyHit) return HISTORICAL_MATCH_SOURCE;
	if (outsideBasket || !marketBasket) return ITEM_MASTER_SOURCE;
	return MARKET_BASKET_SOURCE;
}

export function displayCandidateSource(
	source: string | null | undefined
): string {
	const value = (source ?? '').trim();
	if (/data converter/i.test(value)) return HISTORICAL_MATCH_SOURCE;
	if (/market basket/i.test(value)) return MARKET_BASKET_SOURCE;
	if (/item master/i.test(value)) return ITEM_MASTER_SOURCE;
	return value;
}

export function toCandidate(
	candidate: PartCodingMatchCandidate
): PartCodingCandidate {
	return {
		code: candidate.partCode ?? '',
		description: candidate.rkDescription ?? '',
		matchPercent: candidate.confidence ?? 0,
		inventory: candidate.quantityAvailable ?? 0,
		isMarketBasket: candidate.reasonCode === 'market-basket',
		source: displayCandidateSource(candidate.source)
	};
}

/** One line of status text. The screen shows these in order, one at a time. */
export function codingStageLabels(scope: CodingScope): readonly string[] {
	const labels = ['Parsing the quote…', 'Searching historical matches…'];
	if (scope === 'customer') labels.push('Searching the market basket…');
	labels.push('Searching the item master…');
	return labels;
}

export function sourceLabelFor(rows: readonly PartCodingRow[]): string {
	const sources = [
		...new Set(rows.map((row) => row.source).filter((source) => source))
	];
	return sources.length ? `Source: ${sources.join(', ')}` : 'Source: —';
}

export interface PartCodingFileNotice {
	severity: 'warn' | 'error';
	text: string;
}

const ATTACHMENT_STATUS_NAMES = [
	'Parsed',
	'Truncated',
	'Unsupported',
	'Failed',
	'Skipped'
] as const;

export function attachmentStatusName(status: string | number): string {
	if (typeof status === 'number') {
		return ATTACHMENT_STATUS_NAMES[status] ?? String(status);
	}
	return status;
}

/** Notices for everything that was not fully read. Truncated is a warning. */
export function fileNotices(
	attachments: readonly PartCodingAttachmentOutcome[] | null | undefined
): PartCodingFileNotice[] {
	const notices: PartCodingFileNotice[] = [];
	for (const attachment of attachments ?? []) {
		const status = attachmentStatusName(attachment.status);
		if (status === 'Parsed') continue;
		if (status === 'Truncated') {
			const trimmed = attachment.detail?.trim() ?? '';
			const detail = trimmed
				? trimmed
				: 'Only the first 500 rows were read; some line items may be missing.';
			const text = /split the file/i.test(detail)
				? detail
				: `${detail.replace(/\.$/, '')}. Split the file and upload the rest separately.`;
			notices.push({ severity: 'warn', text });
			continue;
		}
		const trimmed = attachment.detail?.trim() ?? '';
		notices.push({
			severity: 'error',
			text: trimmed
				? trimmed
				: `${attachment.fileName ?? 'That file'} could not be read.`
		});
	}
	return notices;
}

export function fileReadingLabel(fileName: string): string {
	return `Reading ${fileName}…`;
}

/** Shown once a file has been reading for 20 seconds. */
export function fileReadingHint(elapsedSeconds: number): string | null {
	if (elapsedSeconds < 20) return null;
	return 'Large files can take a few minutes. Please keep this page open.';
}

export function formatElapsedClock(totalSeconds: number): string {
	const seconds = Math.max(0, Math.floor(totalSeconds));
	const minutes = Math.floor(seconds / 60);
	const rest = seconds % 60;
	return `${minutes}:${rest.toString().padStart(2, '0')}`;
}
